import {getWithWildcard} from "@o2d/datasource";
import {Issue} from "../domain/entities/issue.entity";
import {ColorBar} from "../views/_shared/types/color-bar.interface";

/** Dot-path pair (with optional [*] wildcard) resolving color/shortcut values on an Issue. */
interface ColorPathConfig {
    pathToColor?: string;
    pathToShortcut?: string;
    /** Feature plan 0074: the text colour next to `pathToColor`, aligned by index. */
    pathToTextColor?: string;
    /**
     * An empty shortcut resolves to `null` (no abbreviation at all) instead of the "??"
     * placeholder. Opt-in per kind: only boards use it, because virtually no board has a
     * shortcut (Anke, 2026-09-26: srv-999 324 of 324 without one) and "??" on every tile marks
     * nothing. Types keep "??" — their shortcut is contract (`timeline.css` keys on "TF"/"U").
     */
    omitEmptyShortcut?: boolean;
}

/** The fixed set of entities every `.timeline-issue` cell resolves color bars for. */
export type EntityColorKind = 'type' | 'employee' | 'board' | 'labels' | 'creator';

export const ENTITY_COLOR_KINDS: readonly EntityColorKind[] = ['type', 'employee', 'board', 'labels', 'creator'];

/**
 * Fixed dot-path mapping from each entity kind to its color/shortcut source on
 * an Issue. Same for every timeline view — no longer per-view configurable
 * (see [[EntityColorKind]]).
 */
export const ENTITY_COLOR_SOURCES: Record<EntityColorKind, ColorPathConfig> = {
    type: {pathToColor: 'type.color', pathToShortcut: 'type.shortcut', pathToTextColor: 'type.textColor'},
    employee: {pathToColor: 'employees[*].color', pathToShortcut: 'employees[*].shortcut', pathToTextColor: 'employees[*].textColor'},
    board: {pathToColor: 'project.color', pathToShortcut: 'project.shortcut', omitEmptyShortcut: true, pathToTextColor: 'project.textColor'},
    labels: {pathToColor: 'keywords[*].color', pathToShortcut: 'keywords[*].shortcut', pathToTextColor: 'keywords[*].textColor'},
    creator: {pathToColor: 'creator.color', pathToShortcut: 'creator.shortcut', pathToTextColor: 'creator.textColor'},
};

/**
 * Resolves ColorBar[] for every entity kind in [[ENTITY_COLOR_SOURCES]] (type,
 * employee, board, labels, creator) from a single Issue, via [[resolveColorBars]].
 */
export function resolveAllEntityColorBars(issue: Issue): Record<EntityColorKind, ColorBar[]> {
    const result = {} as Record<EntityColorKind, ColorBar[]>;
    for (const kind of ENTITY_COLOR_KINDS) {
        result[kind] = resolveColorBars(issue, ENTITY_COLOR_SOURCES[kind]);
    }
    return result;
}

/**
 * Resolves ColorBar[] from an Issue entity based on optional dot-path configuration
 * (pathToColor / pathToShortcut).
 *
 * Synchronous — no EntityManager lookup. The Issue entity is expected to hold
 * complete nested objects (e.g., issue.project, issue.employees) so paths can be
 * traversed directly via getWithWildcard from @o2d/datasource.
 *
 * Behavior:
 * - Neither path configured → []
 * - Only pathToColor → N bars with { color, shortcut: null }
 * - Only pathToShortcut → N bars with { color: null, shortcut|"??"|null } (null: see omitEmptyShortcut below)
 * - Both paths → bars aligned by index; length = max(colors.length, shortcuts.length)
 * - null color value → color: null (rendered as .color-bar--no-color)
 * - null/empty shortcut value → "??" (only when pathToShortcut is configured), or null when
 *   omitEmptyShortcut is set
 * - Empty arrays → []
 *
 * Defensive error handling: any exception thrown by getWithWildcard (invalid
 * path segment, Promise-wrapped property, unexpected entity shape) is caught
 * per path, logged via console.warn, and treated as an empty result for that
 * path. The resolver never throws — a broken path must not crash the renderer.
 */
export function resolveColorBars(
    issue: Issue,
    areaConfig: ColorPathConfig,
): ColorBar[] {
    const {pathToColor, pathToShortcut, pathToTextColor, omitEmptyShortcut = false} = areaConfig;

    // Guard: neither path configured → no bars rendered
    if (!pathToColor && !pathToShortcut) {
        return [];
    }

    const colors = pathToColor ? toArray(safeGetWithWildcard(issue, pathToColor)) : null;
    const shortcuts = pathToShortcut ? toArray(safeGetWithWildcard(issue, pathToShortcut)) : null;
    const textColors = pathToTextColor ? toArray(safeGetWithWildcard(issue, pathToTextColor)) : null;

    const length = Math.max(colors?.length ?? 0, shortcuts?.length ?? 0);
    if (length === 0) {
        return [];
    }

    const bars: ColorBar[] = [];
    for (let i = 0; i < length; i++) {
        const color = colors ? normalizeColor(colors[i]) : null;
        const shortcut = pathToShortcut ? normalizeShortcut(shortcuts ? shortcuts[i] : undefined, omitEmptyShortcut) : null;
        bars.push(textColors ? {color, shortcut, textColor: normalizeColor(textColors[i])} : {color, shortcut});
    }
    return bars;
}

/**
 * Calls getWithWildcard inside a try/catch so that invalid paths or unexpected
 * entity shapes never crash the renderer. On failure returns undefined (which
 * toArray converts to []) and emits a single console.warn for observability.
 */
function safeGetWithWildcard(issue: Issue, path: string): unknown {
    try {
        return getWithWildcard(issue, path);
    } catch (error) {
        console.warn(`[resolveColorBars] Failed to resolve path "${path}" on issue`, {issue, error});
        return undefined;
    }
}

/**
 * Normalizes a raw getWithWildcard result into an array form for uniform iteration.
 * Single values become single-element arrays; undefined/null becomes [].
 */
function toArray(value: unknown): unknown[] {
    if (value === undefined || value === null) {
        return [];
    }
    return Array.isArray(value) ? value : [value];
}

/**
 * Coerces a raw color value to string | null. Non-string values fall back to null
 * so the bar renders as .color-bar--no-color.
 */
function normalizeColor(value: unknown): string | null {
    return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Coerces a raw shortcut value to string. Null, undefined, empty or non-string
 * values become the fallback "??" so the configured slot is always visually
 * marked — or `null` (no abbreviation) when `omitEmpty` is set for that kind.
 */
function normalizeShortcut(value: unknown, omitEmpty: boolean): string | null {
    if (typeof value === "string" && value.length > 0) return value;
    return omitEmpty ? null : "??";
}
